import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Stack } from '@mui/material';

import { Modal } from 'components/Modal';
import { FormProvider, TextField } from 'components/Form';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { useAuth } from 'lib/auth';

import { useCreateNotification } from '../api/createNotification';
import { useUpdateNotification } from '../api/updateNotification';
import { notificationValidationSchema } from '../validation';
import { AttachmentsField, idsNotIn } from './AttachmentFields';
import { InfoRow, ModalActions, textAreaSx } from './ModalActions';

const defaultValues = (notification) => ({
  publish: false,
  text: notification?.text ?? '',
  files: [],
  keptFiles: notification?.files ?? [],
});

export const NotificationFormModal = ({ resource, id, notification, open, handleClose }) => {
  const { user } = useAuth();
  const isEdit = !!notification?.notification_id;

  const methods = useForm({
    defaultValues: defaultValues(notification),
    resolver: useYupValidationResolver(notificationValidationSchema),
  });
  const { mutate: createNotification, isPending: isCreating } = useCreateNotification(
    resource,
    id,
    handleClose
  );
  const { mutate: updateNotification, isPending: isUpdating } = useUpdateNotification(
    resource,
    id,
    handleClose
  );

  const { reset, setValue, handleSubmit } = methods;
  useEffect(() => {
    if (open) reset(defaultValues(notification));
  }, [open, notification, reset]);

  const onSubmit = ({ publish, text, files, keptFiles }) => {
    if (!isEdit) return createNotification({ text, publish, files });

    return updateNotification({
      notificationId: notification.notification_id,
      text,
      publish,
      files,
      remove_file_ids: idsNotIn(
        notification.files,
        keptFiles.map((file) => file.file_id)
      ),
    });
  };

  const submitAs = (publish) => () => {
    setValue('publish', publish);
    handleSubmit(onSubmit)();
  };

  return (
    <Modal
      title={isEdit ? 'Edit notification' : 'Add notification'}
      open={open}
      handleClose={handleClose}
      sx={{ width: 560, overflowY: 'auto' }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Stack gap={1.5} mb={3}>
          <InfoRow
            label="User"
            value={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()}
            note="Anonymized to suppliers"
          />
          <TextField
            fullWidth
            name="text"
            label="Notification"
            helperText="Published notifications are visible to all suppliers and can't be changed"
            multiline
            minRows={6}
            required
            sx={textAreaSx}
          />
          <AttachmentsField keptName={isEdit ? 'keptFiles' : undefined} />
        </Stack>
        <ModalActions
          onCancel={handleClose}
          onDraft={submitAs(false)}
          onSubmit={submitAs(true)}
          disabled={isCreating || isUpdating}
        />
      </FormProvider>
    </Modal>
  );
};
